import React, { useMemo, useState } from 'react';
import MaterialContainer from './MaterialContainer';
import FilterCardHeader from 'components/DesignSystems/FilterHeader/FilterCardHeader';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import MaterialEdit from './MaterialEdit';
import generateUUID from 'utils/sharedUtils/generateUUID';
import { Material } from 'components/IAP/hooks/useSafetyAnalysis';

export const calculateMaterialGridTemplateColumns = (
  numColumns: number
): string => {
  const leftSide = 10; // percentage for the left side column
  const rightSide = 3; // percentage for the right side column
  const count = Math.max(numColumns, 0); // ensure non-negative column count
  const middle = Math.max(100 - leftSide - rightSide, 0); // percentage for the middle columns
  const each = count > 0 ? middle / count : middle; // percentage for each middle column

  // Construct the grid template string
  return `${leftSide}% ${Array.from({ length: count }, () => `${each}%`).join(' ')} ${rightSide}%`.trim();
};

type MaterialListProps = {
  material?: Material[];
  updateMaterials: (material: Material[]) => void;
  readOnly?: boolean;
};

const MaterialList: React.FC<MaterialListProps> = ({
  material = [],
  updateMaterials,
  readOnly = false,
}) => {
  const [search, setSearch] = useState('');
  const [addMaterial, setAddMaterial] = useState(false);

  const filteredMaterials = useMemo(() => {
    if (!search) return material;
    return material.filter(
      (singleMaterial) =>
        singleMaterial?.materialName
          ?.toLowerCase()
          .includes(search.toLowerCase()) ||
        singleMaterial?.niosh?.toLowerCase().includes(search.toLowerCase())
    );
  }, [material, search]);

  const handleAddMaterial = () => {
    setAddMaterial(true);
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-end mb-2 flex-wrap gap-2">
        <div className="flex-grow-1" style={{ minWidth: 240 }}>
          <div>Product or Material Description:</div>
          <input
            className="form-control"
            placeholder="Search Product / Material..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {!readOnly && (
          <div>
            <StylishNewButton
              className="button button--primary"
              onClick={(e) => {
                e.stopPropagation();
                handleAddMaterial();
              }}
            >
              <SharedIcon iconName="add" classes="me-2" />
              Add Material
            </StylishNewButton>
          </div>
        )}
      </div>
      <FilterCardHeader
        gridTemplateColumns={calculateMaterialGridTemplateColumns(9)}
      >
        <p>Name</p>
        <p>Quantity</p>
        <p>Physical State</p>
        <p>NIOSH Number</p>
        <p>Specific Gravity</p>
        <p>pH</p>
        <p>IDLH</p>
        <p>Flash Point (°F)</p>
        <p>LEL</p>
        <p>UEL</p>
      </FilterCardHeader>
      {addMaterial && !readOnly && (
        <MaterialEdit
          singleMaterial={{
            id: generateUUID(),
            materialName: '',
            quantity: 0,
            physState: '',
            niosh: '',
            specificGravity: '',
            ph: 0,
            idlh: '',
            flashPoint: '',
            lel: '',
            uel: '',
          }}
          setIsEditing={setAddMaterial}
          updateMaterial={(newSingleMaterial) => {
            if (updateMaterials) {
              updateMaterials([...material, { ...newSingleMaterial }]);
            }
          }}
        />
      )}
      {filteredMaterials.map((singleMaterial) => (
        <MaterialContainer
          key={`materialContainer_${singleMaterial.id}`}
          singleMaterial={singleMaterial}
          updateMaterial={(updatedMaterial) => {
            const updatedMaterials = material.map((m) =>
              m.id === singleMaterial.id ? { ...m, ...updatedMaterial } : m
            );
            updateMaterials(updatedMaterials);
          }}
          deleteMaterial={(materialId) => {
            const updatedMaterials = material.filter(
              (m) => m.id !== materialId
            );
            updateMaterials(updatedMaterials);
          }}
          readOnly={readOnly}
        />
      ))}
    </>
  );
};

export default MaterialList;
